import type { SourceContentHidden } from "./types";

function stripInlineVisibilityHiddenForGhostRoot(root: HTMLElement) {
  const all = [root, ...root.querySelectorAll<HTMLElement>("*")];
  for (const el of all) {
    if (el.style.visibility === "hidden") el.style.visibility = "";
  }
}

/** 克隆单元格 DOM 供拖拽预览：保留文本/结构，禁用编辑与命中 */
export function cloneCellForDragGhost(sourceCell: HTMLTableCellElement): HTMLTableCellElement {
  const clone = sourceCell.cloneNode(true) as HTMLTableCellElement;
  stripInlineVisibilityHiddenForGhostRoot(clone);
  if (clone.hasAttribute("contenteditable")) {
    clone.setAttribute("contenteditable", "false");
  }
  clone.querySelectorAll("[contenteditable]").forEach((el) => {
    el.setAttribute("contenteditable", "false");
  });
  clone.style.pointerEvents = "none";
  clone.style.userSelect = "none";
  clone.style.boxSizing = "border-box";
  clone.style.border = "1px solid var(--border)";
  clone.style.padding = "0.5rem";
  clone.style.verticalAlign = "top";
  clone.style.minWidth = "80px";
  const w = sourceCell.getBoundingClientRect().width;
  if (w > 0) clone.style.width = `${w}px`;
  return clone;
}

export function buildRowDragGhostTable(sourceTr: HTMLTableRowElement): HTMLTableElement {
  const cloneTr = document.createElement("tr");
  const cells = sourceTr.querySelectorAll<HTMLTableCellElement>(":scope > th, :scope > td");
  cells.forEach((c) => {
    cloneTr.appendChild(cloneCellForDragGhost(c));
  });
  const tbl = document.createElement("table");
  tbl.className = "rt-line-drag-ghost-table w-full table-fixed border-collapse text-sm leading-relaxed";
  tbl.style.width = "100%";
  tbl.style.height = "100%";
  tbl.style.tableLayout = "fixed";
  tbl.style.borderCollapse = "collapse";
  const tb = document.createElement("tbody");
  tb.appendChild(cloneTr);
  tbl.appendChild(tb);
  return tbl;
}

export function buildColDragGhostTable(tableDom: HTMLTableElement, colIndex: number): HTMLTableElement {
  const tbl = document.createElement("table");
  tbl.className = "rt-line-drag-ghost-table w-full table-fixed border-collapse text-sm leading-relaxed";
  tbl.style.width = "100%";
  tbl.style.height = "100%";
  tbl.style.tableLayout = "fixed";
  tbl.style.borderCollapse = "collapse";
  const tb = document.createElement("tbody");
  for (let i = 0; i < tableDom.rows.length; i++) {
    const src = tableDom.rows[i].cells[colIndex] as HTMLTableCellElement | undefined;
    if (!src) continue;
    const tr = document.createElement("tr");
    const cell = cloneCellForDragGhost(src);
    const h = src.getBoundingClientRect().height;
    if (h > 0) cell.style.height = `${h}px`;
    tr.appendChild(cell);
    tb.appendChild(tr);
  }
  tbl.appendChild(tb);
  return tbl;
}

/** 拖拽时源行「清空」：隐藏单元格直接子节点内容（保留占位），并去掉聚焦描边 */
export function hideTableRowCellContents(tr: HTMLTableRowElement): SourceContentHidden[] {
  const rest: SourceContentHidden[] = [];
  tr.querySelectorAll<HTMLTableCellElement>(":scope > th, :scope > td").forEach((cell) => {
    cell.querySelectorAll("[data-rt-cell-focused]").forEach((el) => {
      el.removeAttribute("data-rt-cell-focused");
    });
    Array.from(cell.children).forEach((child) => {
      if (child instanceof HTMLElement) {
        rest.push({ el: child, prevVisibility: child.style.visibility });
        child.style.visibility = "hidden";
      }
    });
  });
  return rest;
}

/** 拖拽时源列「清空」 */
export function hideTableColumnCellContents(tableDom: HTMLTableElement, colIndex: number): SourceContentHidden[] {
  const rest: SourceContentHidden[] = [];
  for (let i = 0; i < tableDom.rows.length; i++) {
    const cell = tableDom.rows[i].cells[colIndex];
    if (!cell) continue;
    cell.querySelectorAll("[data-rt-cell-focused]").forEach((el) => {
      el.removeAttribute("data-rt-cell-focused");
    });
    Array.from(cell.children).forEach((child) => {
      if (child instanceof HTMLElement) {
        rest.push({ el: child, prevVisibility: child.style.visibility });
        child.style.visibility = "hidden";
      }
    });
  }
  return rest;
}

export function restoreSourceContentHidden(rest: SourceContentHidden[] | undefined) {
  rest?.forEach(({ el, prevVisibility }) => {
    el.style.visibility = prevVisibility;
  });
}
